import React, { useState, useEffect, useRef } from 'react';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';
import NewConversationModal from './NewConversationModal';
import api from '../../services/api';
import websocketService from '../../services/websocketService';
import './Chat.css';

const ChatWidget = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const audioRef = useRef(null);
  const isMountedRef = useRef(false);
  const wsHandlersRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const conversationsRef = useRef([]);

  const upsertMessage = (items, message) => {
    if (!message?.id) return items;
    const exists = items.find((msg) => msg.id === message.id);
    if (exists) {
      return items.map((msg) => (msg.id === message.id ? message : msg));
    }
    return [...items, message];
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission;
    }
    return 'denied';
  };

  // Show browser notification
  const showNotification = (title, options) => {
    if (notificationPermission === 'granted') {
      const notification = new Notification(title, options);

      // Focus on the app when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  // Play notification sound
  const playNotificationSound = () => {
    try {
      // Create audio element if it doesn't exist
      if (!audioRef.current) {
        // Using a simple beep sound (base64 encoded)
        // This is a short notification beep sound
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800; // Frequency in Hz
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      }
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  // Keep refs in sync with state (to avoid stale closures in WebSocket handlers)
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // Fetch conversations and setup WebSocket on mount
  useEffect(() => {
    isMountedRef.current = true;
    fetchConversations();
    setupWebSocket();

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      requestNotificationPermission();
    } else if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (wsHandlersRef.current) {
        websocketService.off('message', wsHandlersRef.current.onMessage);
        websocketService.off('conversationUpdate', wsHandlersRef.current.onConversationUpdate);
        websocketService.off('connected', wsHandlersRef.current.onConnected);
        websocketService.off('disconnected', wsHandlersRef.current.onDisconnected);
        websocketService.off('error', wsHandlersRef.current.onError);
      }
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (selectedConversation) return;

    const pendingId = sessionStorage.getItem('chat:openConversationId');
    if (!pendingId) return;

    const existing = conversations.find((conv) => conv.id === pendingId);
    if (existing) {
      setSelectedConversation(existing);
      sessionStorage.removeItem('chat:openConversationId');
      return;
    }

    (async () => {
      try {
        const response = await api.get(`/v1/conversations/conversations/${pendingId}/`);
        const newConv = Array.isArray(response.data)
          ? normalizeConversations(response.data)[0]
          : normalizeConversationItem(response.data);
        setConversations((prev) => {
          const merged = prev.map((conv) => (conv.id === newConv.id ? newConv : conv));
          return dedupeConversations([newConv, ...merged]);
        });
        setSelectedConversation(newConv);
        sessionStorage.removeItem('chat:openConversationId');
      } catch (error) {
        console.error('Error opening conversation from toast:', error);
      }
    })();
  }, [loading, conversations, selectedConversation]);

  // Fetch messages when conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  const normalizeConversationItem = (conversation) => {
    const lastMessageText =
      typeof conversation.last_message === 'string'
        ? conversation.last_message
        : conversation.last_message?.message || '';

    if (lastMessageText) {
      return { ...conversation, last_message_text: lastMessageText };
    }

    const messages = Array.isArray(conversation.messages) ? conversation.messages : [];
    const lastMessage = messages[messages.length - 1];
    return {
      ...conversation,
      last_message_text: lastMessage?.message || '',
      last_message_at: conversation.last_message_at || lastMessage?.created_at || conversation.last_message_at,
    };
  };

  const normalizeConversations = (data) => {
    const items = Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
      ? data.results
      : [];

    return items.map(normalizeConversationItem);
  };

  const dedupeConversations = (items) => {
    const map = new Map();
    items.forEach((item) => {
      if (item?.id) {
        map.set(item.id, item);
      }
    });
    return Array.from(map.values());
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/conversations/conversations/');
      setConversations(dedupeConversations(normalizeConversations(response.data)));
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await api.get(`/v1/conversations/conversations/${conversationId}/`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const setupWebSocket = () => {
    // Use global WebSocket connection from MainLayout; only attach listeners here.

    // Handle new messages
    const onMessage = async (newMessage) => {
      // Use refs to get current values (avoid stale closures)
      const currentSelectedConversation = selectedConversationRef.current;
      const currentConversations = conversationsRef.current;

      // Only show notification and play sound if it's not the currently selected conversation
      // or if the window doesn't have focus
      const shouldNotify =
        !currentSelectedConversation ||
        newMessage.conversation !== currentSelectedConversation.id ||
        !document.hasFocus();

      if (shouldNotify && newMessage.direction === 'inbound') {
        // Find the conversation to get contact name
        const conversation = currentConversations.find(c => c.id === newMessage.conversation);
        const contactName = conversation?.contact?.name || 'Unknown';
        const messagePreview = newMessage.message.substring(0, 100);

        // Show browser notification
        showNotification(`New message from ${contactName}`, {
          body: messagePreview,
          icon: '/logo192.png', // You can customize this icon
          tag: `msg-${newMessage.id}`,
        });

        // Play notification sound
        playNotificationSound();
      }

      // Add message to list if this conversation is selected
      if (currentSelectedConversation && newMessage.conversation === currentSelectedConversation.id) {
        setMessages((prevMessages) => upsertMessage(prevMessages, newMessage));
      }

      // Update conversation list
      setConversations((prevConversations) => {
        const existing = prevConversations.find((conv) => conv.id === newMessage.conversation);
        if (!existing) {
          return prevConversations;
        }

        return dedupeConversations(prevConversations.map((conv) =>
          conv.id === newMessage.conversation
            ? {
                ...conv,
                last_message: newMessage.message,
                last_message_text: newMessage.message,
                last_message_at: newMessage.created_at,
                unread_count: currentSelectedConversation?.id === conv.id ? 0 : conv.unread_count + 1,
              }
            : conv
        ));
      });

      // If this is a new conversation, fetch it and upsert
      try {
        const response = await api.get(`/v1/conversations/conversations/${newMessage.conversation}/`);
        const newConv = Array.isArray(response.data)
          ? normalizeConversations(response.data)[0]
          : normalizeConversationItem(response.data);
        setConversations((prev) => {
          const merged = prev.map((conv) => (conv.id === newConv.id ? newConv : conv));
          return dedupeConversations([newConv, ...merged]);
        });
      } catch (error) {
        console.error('Error fetching new conversation:', error);
      }
    };

    // Handle conversation updates
    const onConversationUpdate = (updatedConversation) => {
      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          conv.id === updatedConversation.id ? updatedConversation : conv
        )
      );
    };

    // Handle connection events
    const onConnected = () => {
      setWsConnected(true);
    };

    const onDisconnected = () => {
      setWsConnected(false);
    };

    const onError = () => {
      setWsConnected(false);
    };

    if (wsHandlersRef.current) {
      websocketService.off('message', wsHandlersRef.current.onMessage);
      websocketService.off('conversationUpdate', wsHandlersRef.current.onConversationUpdate);
      websocketService.off('connected', wsHandlersRef.current.onConnected);
      websocketService.off('disconnected', wsHandlersRef.current.onDisconnected);
      websocketService.off('error', wsHandlersRef.current.onError);
    }

    websocketService.on('message', onMessage);
    websocketService.on('conversationUpdate', onConversationUpdate);
    websocketService.on('connected', onConnected);
    websocketService.on('disconnected', onDisconnected);
    websocketService.on('error', onError);

    wsHandlersRef.current = {
      onMessage,
      onConversationUpdate,
      onConnected,
      onDisconnected,
      onError,
    };
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
  };

  const handleSendMessage = async (conversationId, messageText) => {
    try {
      const response = await api.post(
        `/v1/conversations/conversations/${conversationId}/send_message/`,
        { message: messageText }
      );

      // Add new message to the list
      setMessages((prevMessages) => upsertMessage(prevMessages, response.data));

      // Update conversation last message
      setConversations(
        conversations.map((conv) =>
          conv.id === conversationId
            ? {
                ...conv,
                last_message: messageText,
                last_message_text: messageText,
                last_message_at: new Date(),
              }
            : conv
        )
      );
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleMarkRead = async (conversationId) => {
    try {
      await api.post(`/v1/conversations/conversations/${conversationId}/mark_read/`);

      // Update conversation unread count
      setConversations(
        conversations.map((conv) =>
          conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
        )
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleStartConversation = async (phoneNumber, contactName, initialMessage) => {
    try {
      const response = await api.post('/v1/conversations/conversations/start_conversation/', {
        phone_number: phoneNumber,
        contact_name: contactName,
        message: initialMessage,
      });

      const newConversation = normalizeConversationItem(response.data.conversation);

      // Add or update conversation in list
      setConversations((prev) => {
        const exists = prev.find((c) => c.id === newConversation.id);
        if (exists) {
          return prev.map((c) => (c.id === newConversation.id ? newConversation : c));
        }
        return [newConversation, ...prev];
      });

      // Select the new conversation
      setSelectedConversation(newConversation);
      setShowNewConversationModal(false);

      // Fetch messages if there are any
      if (newConversation.id) {
        fetchMessages(newConversation.id);
      }

      return { success: true, conversation: newConversation };
    } catch (error) {
      console.error('Error starting conversation:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="chat-widget loading">
        <div className="loading-spinner">Loading conversations...</div>
      </div>
    );
  }

  return (
    <div className="chat-widget">
      <ConversationList
        conversations={conversations}
        selectedConversation={selectedConversation}
        onSelectConversation={handleSelectConversation}
        onNewConversation={() => setShowNewConversationModal(true)}
      />
      <ChatWindow
        conversation={selectedConversation}
        messages={messages}
        onSendMessage={handleSendMessage}
        onMarkRead={handleMarkRead}
      />
      <NewConversationModal
        open={showNewConversationModal}
        onClose={() => setShowNewConversationModal(false)}
        onStartConversation={handleStartConversation}
      />
    </div>
  );
};

export default ChatWidget;
